import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PageHeader } from '@bayclubs/horizon';
import { TranslateService } from '@ngx-translate/core';
import { PageHeaderService } from '@shared/components/page-header/page-header.service';
import { filter } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-page-header',
    imports: [
        NgClass, PageHeader
    ],
    templateUrl: './page-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageHeaderComponent {

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly pageHeader = inject(PageHeaderService);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);

    readonly actionLabel = computed(() => this.pageHeader.actionLabel());
    readonly containerClass = computed(() => this.routeMeta()?.containerClass ?? null);
    readonly title = computed(() => this.translateTitle(this.routeMeta()?.title ?? null));

    private readonly routeMeta = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(() => this.getDeepestRouteMeta(this.activatedRoute))
        ),
        { initialValue: this.getDeepestRouteMeta(this.activatedRoute) }
    );

    onActionClick(): void {
        this.pageHeader.action()?.();
    }

    private getDeepestRouteMeta(route: ActivatedRoute): { title: string | null; containerClass: string | null; } {
        let current: ActivatedRoute | null = route;

        let lastTitle = current?.snapshot?.data?.['title'] ?? null;
        let lastContainerClass = current?.snapshot?.data?.['containerClass'] ?? null;

        while (current?.firstChild) {
            current = current.firstChild;

            const { title, containerClass, pageHeaderAction } = current.snapshot.data ?? {};

            if (title) {
                lastTitle = title;
            }
            if (containerClass) {
                lastContainerClass = containerClass;
            }
        }

        return { title: lastTitle, containerClass: lastContainerClass };
    }

    private translateTitle(keyOrText: string | null): string | null {
        if (!keyOrText) {
            return null;
        }
        const translated = this.translateService.instant(keyOrText);
        if (typeof translated === 'string') {
            return translated === keyOrText ? keyOrText : translated;
        }
        return keyOrText;
    }

}
